import { Connector } from '@web3-react/types'
import type { Actions, Provider } from '@web3-react/types'
import { getAddress } from 'ethers/lib/utils'
import { createAppKit } from '@reown/appkit/react'
import { Ethers5Adapter } from '@reown/appkit-adapter-ethers5'
import { pulsechain } from '@reown/appkit/networks'

const projectId = process.env.REACT_APP_WALLET_CONNECT_PROJECT_ID as string
if (!projectId) console.warn('Missing REACT_APP_WALLET_CONNECT_PROJECT_ID')

const metadata = {
  name: 'Piteas Widget',
  description: 'Swap Widget | Piteas Dex Aggregator',
  url: 'https://widget.piteas.io',
  icons: ['https://widget.piteas.io/logo.png'],
}

const networks = [pulsechain] as [typeof pulsechain, ...typeof pulsechain[]]

const appKit = createAppKit({
  adapters: [new Ethers5Adapter()],
  projectId,
  metadata,
  networks,
  defaultNetwork: pulsechain,
  themeMode: 'dark',
  enableWalletGuide: false,
  enableReconnect: false,
  featuredWalletIds: [
    '18388be9ac2d02726dbac9777c96efaac06d744b2f6d580fccdd4127a6d01fd1', // Rabby
    '5d9f1395b3a8e848684848dc4147cbd05c8d54bb737eac78fe103901fe6b01a1', // OKX
    'dd43441a6368ec9046540c46c5fdc58f79926d17ce61a176444568ca7c970dcd', // Internet Money
    'a2eb8a1c403a4440b2f578e9deb185b8e22cf4ec2a2a58441032b84b13aaab87', // Pulse Wallet
  ],
  excludeWalletIds: ['c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96'], // Metamask already injected
})

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))
const toHex = (id: number) => '0x' + id.toString(16)
const hexToDec = (id: string | number) => (typeof id === 'string' ? parseInt(id, 16) : Number(id))

function readStatus() {
  const s = (appKit as any)?.state ?? (appKit as any)?._state
  return {
    address: s?.account?.address as `0x${string}` | undefined,
    chainId: s?.network?.chainId as number | undefined,
    walletProvider: s?.walletProvider as Provider | undefined,
  }
}

function clearReownPersistentState() {
  try {
    const ls = window?.localStorage
    if (!ls) return
    const keys: string[] = []
    for (let i = 0; i < ls.length; i++) keys.push(ls.key(i)!)

    for (const k of keys) {
      // WC v2, old WC keys, Web3Modal/AppKit/Reown caches
      if (/^wc@/i.test(k) || /walletconnect/i.test(k) || /w3m|web3modal|appkit|reown/i.test(k)) {
        ls.removeItem(k)
      }
    }
  } catch {}
}

async function waitForAppKitStatus(timeoutMs = 3000, everyMs = 100) {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    const st = readStatus()
    if (st.walletProvider && st.address && st.chainId) return st
    await sleep(everyMs)
  }
  return readStatus()
}

async function switchEvmChain(provider: any, desiredChainId: number) {
  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: toHex(desiredChainId) }],
    })
  } catch (err: any) {
    if (err?.code === 4902 && desiredChainId === 369) {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: toHex(369),
            chainName: 'PulseChain',
            nativeCurrency: { name: 'Pulse', symbol: 'PLS', decimals: 18 },
            rpcUrls: [process.env.REACT_APP_PULSECHAIN_RPC ?? 'https://rpc.pulsechain.com'],
            blockExplorerUrls: ['https://scan.pulsechain.com'],
          },
        ],
      })
    } else {
      throw err
    }
  }
}

export class ReownConnector extends Connector {
  private removeListeners?: () => void

  constructor(actions: Actions, onError?: (error: Error) => void) {
    super(actions, onError)
  }

  private bindProviderEvents(p: any) {
    if (!p?.on) return
    const onAccountsChanged = (accs: string[]) => {
      this.actions.update({ accounts: (accs ?? []).map((a) => getAddress(a)) })
    }
    const onChainChanged = (id: string | number) => {
      const n = hexToDec(id)
      if (Number.isFinite(n)) this.actions.update({ chainId: Number(n) })
    }
    const onDisconnect = () => this.actions.resetState()

    p.on('accountsChanged', onAccountsChanged)
    p.on('chainChanged', onChainChanged)
    p.on('disconnect', onDisconnect)

    this.removeListeners = () => {
      p.removeListener?.('accountsChanged', onAccountsChanged)
      p.removeListener?.('chainChanged', onChainChanged)
      p.removeListener?.('disconnect', onDisconnect)
    }
  }

  // Silent auto-connect (no modal)
  async connectEagerly(desiredChainId?: number): Promise<void> {
    const st = readStatus()
    if (st.walletProvider && st.address && st.chainId) {
      this.provider = st.walletProvider
      this.bindProviderEvents(st.walletProvider as any)

      let finalId = st.chainId
      if (desiredChainId && finalId !== desiredChainId) {
        await switchEvmChain(st.walletProvider as any, desiredChainId)
        await sleep(100)
        const finalHex = await (st.walletProvider as any).request({ method: 'eth_chainId' }).catch(() => undefined)
        finalId = typeof finalHex === 'string' ? parseInt(finalHex, 16) : desiredChainId
      }
      this.actions.update({ chainId: finalId ?? 369, accounts: [getAddress(st.address)] })
      return
    }
    this.actions.resetState()
  }


  async activate(desiredChainId?: number, userInitiated = true): Promise<void> {
    this.actions.startActivation?.()

    // 1) reuse existing provider
    if (this.provider?.request) {
      if (desiredChainId) {
        await switchEvmChain(this.provider, desiredChainId)
        await sleep(100)
      }
      const [accounts, chainHex] = await Promise.all([
        this.provider.request({ method: 'eth_accounts' }) as Promise<string[]>,
        this.provider.request({ method: 'eth_chainId' }) as Promise<string>,
      ])
      const chainId = hexToDec(chainHex)
      this.bindProviderEvents(this.provider as any)
      this.actions.update({ chainId: desiredChainId ?? chainId, accounts: (accounts ?? []).map(getAddress) })
      return
    }

    // 2) reuse previous AppKit session silently — only for non-user-initiated activation (eager connect)
    if (!userInitiated) {
      const st = readStatus()
      if (st.walletProvider && st.address && st.chainId) {
        this.provider = st.walletProvider
        this.bindProviderEvents(st.walletProvider as any)
        this.actions.update({ chainId: st.chainId, accounts: [getAddress(st.address)] })
        return
      }
    }

    // 3) open modal only if user clicked
    if (userInitiated) {
      try {
        ;(appKit as any)?.open?.()
      } catch {}
    }

    function detectIsMetaMask(p: any) {
      return !!(p?.isMetaMask && !p?.isBraveWallet && !p?.isRabby && !p?.isLedgerConnect && !p?.isTrustWallet)
    }

    function detectIsOkx(p: any) {
      return !!(
        p?.isOkxWallet ||
        p?.isOKExWallet ||
        (typeof window !== 'undefined' && (window as any)?.okxwallet) ||
        p?.__OKX
      )
    }

    async function pickInjectedForUserClick(): Promise<any | undefined> {
      const providers = enumerateInjectedProviders()
      if (!providers.length) return undefined
      const hasMM = providers.some(detectIsMetaMask)
      const okx = providers.find(detectIsOkx)
      if (okx && !hasMM) return okx
      if (providers.length === 1) return providers[0]
      return undefined
    }

    function enumerateInjectedProviders(): any[] {
      const w = window as any
      const list: any[] = []

      if (Array.isArray(w.ethereum?.providers)) list.push(...w.ethereum.providers)
      if (w.ethereum && !list.includes(w.ethereum)) list.push(w.ethereum)

      if (w.okxwallet?.ethereum && !list.includes(w.okxwallet.ethereum)) list.push(w.okxwallet.ethereum)
      if (w.okxwallet?.provider && !list.includes(w.okxwallet.provider)) list.push(w.okxwallet.provider)
      if (w.okxwallet && !list.includes(w.okxwallet)) list.push(w.okxwallet)

      if (w.coinbaseWalletExtension && !list.includes(w.coinbaseWalletExtension)) list.push(w.coinbaseWalletExtension)
      if (w.trustwallet && !list.includes(w.trustwallet)) list.push(w.trustwallet)
      if (w.rabby && !list.includes(w.rabby)) list.push(w.rabby)

      return list.filter(Boolean)
    }

    async function findAuthorizedProviderAndAccount(): Promise<{
      provider?: any
      address?: `0x${string}`
      chainId?: number
    }> {
      const providers = enumerateInjectedProviders()
      for (const p of providers) {
        try {
          const accs: string[] = await p.request({ method: 'eth_accounts' })
          if (Array.isArray(accs) && accs.length > 0) {
            const chainHex = await p.request({ method: 'eth_chainId' })
            const chainId = typeof chainHex === 'string' ? parseInt(chainHex, 16) : Number(chainHex)
            const address = getAddress(accs[0]) as `0x${string}`
            return { provider: p, address, chainId }
          }
        } catch {}
      }
      return {}
    }

    // 4) wait for provider from AppKit
    let provider: Provider | undefined = (appKit as any)?.getProvider?.()
    if (!provider) {
      const s = await waitForAppKitStatus()
      provider = (s.walletProvider as any) ?? undefined
      if (!provider && !userInitiated) {
        // 5) fallback: only during eager flow, never when user clicked the Reown button
        const found = await findAuthorizedProviderAndAccount()
        if (found.provider) {
          provider = found.provider
        }
      }
    }

    if (!provider && userInitiated) {
      const injected = await pickInjectedForUserClick()
      if (injected) {
        provider = injected as any
      }
    }

    if (!provider) {
      this.actions.resetState()
      throw new Error('User closed modal')
    }

    this.provider = provider
    this.bindProviderEvents(provider as any)

    // 6) request accounts + chain
    const accountsPromise = provider.request?.({ method: 'eth_requestAccounts' }) as Promise<string[]>
    const chainPromise = provider.request?.({ method: 'eth_chainId' }) as Promise<string>
    const [accounts, chainHex] = await Promise.all([accountsPromise, chainPromise])
    let chainId = hexToDec(chainHex)

    if (!accounts || accounts.length === 0) {
      this.actions.resetState()
      throw new Error('No accounts')
    }

    if (desiredChainId && chainId !== desiredChainId) {
      await switchEvmChain(provider as any, desiredChainId)
      chainId = desiredChainId
    }

    this.actions.update({ chainId, accounts: accounts.map(getAddress) })
  }

  async deactivate(): Promise<void> {
    this.removeListeners?.()
    this.removeListeners = undefined
    this.provider = undefined
    this.actions.resetState()
    const anyKit = appKit as any
    await anyKit?.disconnect?.().catch(() => undefined)
    await anyKit?.reset?.().catch(() => undefined)
    clearReownPersistentState()
  }
}
